import type * as models from '../models'

import type { Column } from './column'

/**
 * Row configuration and cells resolved for direct template binding.
 *
 * This is the final row view model. It contains no consumer-owned row shape
 * and no unresolved callbacks that the template would need to calculate.
 */
export interface Row {
  readonly cells: readonly Row.Cell[]
  readonly expanded: boolean
  readonly hasChildren: boolean
  readonly indentPx: number
  readonly onClick?: () => void
  readonly rowId: models.Row.Id
  readonly selected: boolean
  readonly selectionDisabled: boolean
  readonly styles?: models.Styles
  readonly tools: readonly models.Tool[]
}

export namespace Row {
  /** Width of one legacy tool icon box (24px icon + 5px padding per side). */
  const ROW_TOOL_WIDTH_PX = 34
  /** Side padding applied once around the whole tool group. */
  const ROW_TOOLS_PADDING_WIDTH_PX = 10
  const ROW_INDENT_WIDTH_PX = 20

  /** Cell configuration resolved for one row and column. */
  export interface Cell {
    readonly align: models.Column.Alignment
    readonly columnId: models.Column.Id
    readonly componentRenderer?: models.Renderer.Component
    readonly css?: models.Column.Cell['css']
    readonly contentOffsetPx: number
    readonly justifyContent?: 'center' | 'flex-end'
    readonly onClick?: () => void
    readonly value: models.Column.Cell['value']
    readonly valueInfo?: models.Renderer.Value['valueInfo']
  }

  /**
   * Row after the consumer adapter has run, but before hierarchy visibility is
   * applied. `rowIndex` remains the index from the source collection so row
   * callbacks and identity functions receive stable source coordinates.
   */
  export interface Source<TRow> {
    readonly onClick?: () => void
    readonly parentId?: models.Row.Id
    readonly row: TRow
    readonly rowId: models.Row.Id
    readonly rowIndex: number
    readonly styles?: models.Styles
    readonly tools: readonly models.Tool[]
  }

  /** Row carrying the hierarchy metadata needed by the final view model. */
  export interface VisibleSource<TRow> extends Source<TRow> {
    readonly depth: number
    readonly expanded: boolean
    readonly hasChildren: boolean
    readonly hierarchical: boolean
  }

  /**
   * Applies the consumer row adapter before hierarchy filtering.
   *
   * Identity and row description are evaluated once per source row. The
   * resulting source model keeps the raw entity together with normalized
   * parent/tool/click metadata for the later hierarchy and cell passes.
   */
  export function getSource<TRow>(
    rowsConfig: models.Row.Config<TRow> | undefined,
    hierarchy: models.Row.Hierarchy<TRow> | undefined,
  ): readonly Source<TRow>[] {
    if (!rowsConfig) {
      return []
    }

    return rowsConfig.items.map((row, rowIndex) => {
      const rowId = rowsConfig.getId(row, rowIndex)
      const description = rowsConfig.describe?.({ row, rowId, rowIndex }) ?? {}

      return {
        onClick: description.onClick,
        parentId: hierarchy?.getParentId(row, rowIndex) ?? undefined,
        row,
        rowId,
        rowIndex,
        styles: description.styles,
        tools: description.tools ?? [],
      }
    })
  }

  /**
   * Builds the visible depth-first row list from rows whose parent matches the
   * configured root ID, or has no parent when no root is configured.
   *
   * The rows are first indexed by parent ID so expanding a branch does not
   * repeatedly scan the entire source list. The active-path guard prevents a
   * malformed parent cycle from recursing indefinitely while preserving the
   * source sibling order for valid trees.
   */
  export function getVisible<TRow>(
    sourceRows: readonly Source<TRow>[],
    hierarchy: models.Row.Hierarchy<TRow> | undefined,
    expandedRowIds: ReadonlySet<models.Row.Id>,
  ): readonly VisibleSource<TRow>[] {
    if (!hierarchy) {
      // Flat tables avoid all parent indexing and expansion work.
      return sourceRows.map(row => ({
        ...row,
        depth: 0,
        expanded: false,
        hasChildren: false,
        hierarchical: false,
      }))
    }

    const rowsByParentId = new Map<models.Row.Id | undefined, Source<TRow>[]>()
    for (const row of sourceRows) {
      const siblings = rowsByParentId.get(row.parentId) ?? []
      siblings.push(row)
      rowsByParentId.set(row.parentId, siblings)
    }

    const visibleRows: VisibleSource<TRow>[] = []
    const activePath = new Set<models.Row.Id>()
    // Only roots under the configured root ID are traversed. Children of a
    // collapsed root remain indexed but are intentionally absent from output.
    const rootRows = rowsByParentId.get(hierarchy.rootId ?? undefined) ?? []
    for (const row of rootRows) {
      appendVisibleRow(
        row,
        0,
        rowsByParentId,
        expandedRowIds,
        visibleRows,
        activePath,
      )
    }

    return visibleRows
  }

  /**
   * Resolves cells and hierarchy presentation for the visible rows.
   *
   * Selection is converted to a Set once for the whole pass. Cell callbacks
   * receive the calculated column/row coordinates, and renderer values are
   * split into component versus value branches so the template only selects a
   * prepared outlet.
   */
  export function getApplied<TRow>(
    columns: readonly Column<TRow>[],
    visibleRows: readonly VisibleSource<TRow>[],
    selection: models.Selection<TRow> | undefined,
  ): readonly Row[] {
    const selectedIds = new Set(selection?.selectedIds)

    return visibleRows.map(sourceRow => ({
      cells: columns.map(({ align, config, id }, columnIndex) => {
        const cell = config.getCell({
          columnId: id,
          row: sourceRow.row,
          rowId: sourceRow.rowId,
          rowIndex: sourceRow.rowIndex,
        })
        const componentRenderer =
          cell.renderer?.type === 'component' ? cell.renderer : undefined
        const valueInfo =
          cell.renderer?.type === 'value' ? cell.renderer.valueInfo : undefined

        return {
          align,
          columnId: id,
          componentRenderer,
          css: cell.css,
          // Only the first hierarchical cell receives the indentation offset;
          // later cells retain their normal content position.
          contentOffsetPx:
            columnIndex === 0 && sourceRow.hierarchical
              ? sourceRow.depth * ROW_INDENT_WIDTH_PX + ROW_INDENT_WIDTH_PX
              : 0,
          justifyContent:
            align === 'center'
              ? 'center'
              : align === 'right'
                ? 'flex-end'
                : undefined,
          onClick: cell.onClick,
          value: cell.value,
          valueInfo,
        }
      }),
      expanded: sourceRow.expanded,
      hasChildren: sourceRow.hasChildren,
      indentPx: sourceRow.depth * ROW_INDENT_WIDTH_PX,
      onClick: sourceRow.onClick,
      rowId: sourceRow.rowId,
      selected: selectedIds.has(sourceRow.rowId),
      selectionDisabled:
        selection?.isDisabled?.({
          row: sourceRow.row,
          rowId: sourceRow.rowId,
          rowIndex: sourceRow.rowIndex,
        }) ?? false,
      styles: sourceRow.styles,
      tools: sourceRow.tools,
    }))
  }

  /**
   * Width reserved for the largest row-tool set.
   *
   * Every row shares one tools track, so the maximum tool count—not each
   * row's individual count—determines the reserved width.
   */
  export function getToolsWidth<TRow>(rows: readonly Source<TRow>[]): number {
    const maximumToolCount = rows.reduce(
      (maximumCount, row) => Math.max(maximumCount, row.tools.length),
      0,
    )

    return maximumToolCount
      ? maximumToolCount * ROW_TOOL_WIDTH_PX + ROW_TOOLS_PADDING_WIDTH_PX
      : 0
  }

  /**
   * Appends one expanded branch while preserving source sibling order.
   *
   * `activePath` tracks only the recursion path, not every visited row. It is
   * therefore safe for two valid branches to reference the same child while
   * still stopping a cycle such as A -> B -> A.
   */
  function appendVisibleRow<TRow>(
    row: Source<TRow>,
    depth: number,
    rowsByParentId: ReadonlyMap<
      models.Row.Id | undefined,
      readonly Source<TRow>[]
    >,
    expandedRowIds: ReadonlySet<models.Row.Id>,
    visibleRows: VisibleSource<TRow>[],
    activePath: Set<models.Row.Id>,
  ): void {
    if (activePath.has(row.rowId)) {
      // A cycle should hide only the recursive edge, not discard the already
      // rendered ancestor branch.
      return
    }

    const children = rowsByParentId.get(row.rowId) ?? []
    const expanded = expandedRowIds.has(row.rowId)
    visibleRows.push({
      ...row,
      depth,
      expanded,
      hasChildren: children.length > 0,
      hierarchical: true,
    })

    if (!expanded) {
      return
    }

    activePath.add(row.rowId)
    for (const child of children) {
      appendVisibleRow(
        child,
        depth + 1,
        rowsByParentId,
        expandedRowIds,
        visibleRows,
        activePath,
      )
    }
    activePath.delete(row.rowId)
  }
}
